import { emitirGiftcard, listarVigentes, canjearGiftcard, listarGiftcards, anularGiftcard } from '../infrastructure/giftcards.repository.js';
import { normalizar } from '../shared/faq-search-policy.js';
import { formatFechaDia } from '../utils/formatters.js';

/**
 * Gift cards del mostrador (spec staff-giftcards). Los límites (monto,
 * largos) y los estados son del servidor (migración 35): acá sólo se arma
 * lo que se manda, se traducen los rechazos a texto y se adapta lo que
 * vuelve a lo que pintan las vistas y la imagen.
 */

/** Atajos de monto del formulario (D5). No son un límite: el límite es del servidor. */
export const MONTOS_RAPIDOS = [15000, 20000, 25000, 30000, 40000, 50000, 75000, 100000];

export const MEDIOS = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'tarjeta', label: 'Tarjeta' },
];

export const ESTADOS = [
    { value: 'vigentes', label: 'Vigentes' },
    { value: 'canjeadas', label: 'Canjeadas' },
    { value: 'vencidas', label: 'Vencidas' },
    { value: 'anuladas', label: 'Anuladas' },
];

const GENERICO = 'Ocurrió un error. Probá de nuevo.';
const EMITIR_INCIERTO = 'Se cortó la conexión y no sabemos si se emitió. Antes de reintentar, fijate en Canjear si ya está en la lista; si está, pedile la imagen a Caro (Gift cards, en admin).';

/**
 * The amount as typed at the counter: "30000", "30.000" (Argentine thousands
 * dots) or "$ 30.000". A comma is a decimal separator, so "15000,5" stays a
 * fraction and the server refuses it. The limits are the server's.
 * @param {string} texto
 * @returns {number}
 */
function parsearMonto(texto) {
    const limpio = texto.replace(/^\$\s*/, '');
    if (/^\d{1,3}(\.\d{3})+$/.test(limpio)) return Number(limpio.replace(/\./g, ''));
    return Number(limpio.replace(',', '.'));
}
const SIN_PERMISO = 'Tu sesión no tiene permiso para esto.';

const EMITIR_MESSAGES = {
    monto_invalido: 'El monto tiene que ser de $15.000 a $1.000.000, sin centavos.',
    medio_invalido: 'Elegí cómo pagó: efectivo, transferencia o tarjeta.',
    para_requerido: 'Poné para quién es la gift card.',
    nombre_largo: 'Los nombres pueden tener hasta 60 caracteres.',
    mensaje_largo: 'El mensaje puede tener hasta 200 caracteres.',
};

const CANJEAR_MESSAGES = {
    ya_canjeada: 'Esta gift card ya fue canjeada.',
    vencida: 'Esta gift card está vencida.',
    anulada: 'Esta gift card fue anulada.',
    giftcard_inexistente: 'Esa gift card ya no existe.',
};

const ANULAR_MESSAGES = {
    motivo_requerido: 'Escribí el motivo.',
    motivo_largo: 'El motivo puede tener hasta 500 caracteres.',
    ya_canjeada: 'No se puede anular: ya fue canjeada.',
    ya_anulada: 'Esa gift card ya estaba anulada.',
    giftcard_inexistente: 'Esa gift card ya no existe.',
};

/**
 * The one mapping of a thrown error to a message: 42501 is always "no
 * permission"; anything else gets the caller's fallback.
 * @param {unknown} err @param {string} [fallback] @returns {string}
 */
function mensajeDeError(err, fallback = GENERICO) {
    return /** @type {{ code?: string }} */ (err)?.code === '42501' ? SIN_PERMISO : fallback;
}

/** @param {unknown} err @param {string} donde */
function reportar(err, donde) {
    window.Sentry?.captureException(err);
    // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
    console.error(`giftcards: ${donde}`, err);
}

/** @param {string | null | undefined} texto @returns {string | null} */
const opcional = (texto) => String(texto ?? '').trim() || null;

/** @param {string} medio @returns {string} */
export function etiquetaMedio(medio) {
    return MEDIOS.find((m) => m.value === medio)?.label ?? medio;
}

/**
 * Lo que pinta la imagen (spec B3), a partir de la gift card que devolvió SQL.
 * @param {Record<string, any>} g
 * @returns {{ para: string, deParte: string | null, mensaje: string | null, monto: number, validaHasta: string }}
 */
export function modeloImagen(g) {
    return {
        para: g.para,
        deParte: g.de_parte ?? null,
        mensaje: g.mensaje ?? null,
        monto: Number(g.monto),
        validaHasta: formatFechaDia(g.valida_hasta),
    };
}

/**
 * @param {{ monto: string | number, medio: string, para: string, deParte?: string, mensaje?: string }} datos
 * @returns {Promise<{ ok: true, giftcard: Record<string, any>, imagen: ReturnType<typeof modeloImagen> } | { ok: false, mensaje: string }>}
 */
export async function emitir(datos) {
    const textoMonto = String(datos.monto ?? '').trim();
    const monto = parsearMonto(textoMonto);
    const para = opcional(datos.para);
    if (textoMonto === '' || !Number.isFinite(monto) || !para) {
        return { ok: false, mensaje: 'Poné el monto y para quién es.' };
    }
    let data;
    try {
        data = await emitirGiftcard({ monto, medio: datos.medio, para, deParte: opcional(datos.deParte), mensaje: opcional(datos.mensaje) });
    } catch (err) {
        reportar(err, 'no se pudo emitir');
        // No answer ≠ not issued: with no server code (dropped connection,
        // timeout) the card may be stored and paid, and a blind retry would
        // issue a second one (review resilience-1). A server error with a code
        // never ran the insert.
        return { ok: false, mensaje: mensajeDeError(err, err?.code ? GENERICO : EMITIR_INCIERTO) };
    }
    if (data?.ok && data.giftcard) return { ok: true, giftcard: data.giftcard, imagen: modeloImagen(data.giftcard) };
    if (!EMITIR_MESSAGES[data?.error]) reportar(new Error(`emitir_giftcard: respuesta inesperada ${JSON.stringify(data)}`), 'respuesta inesperada');
    return { ok: false, mensaje: EMITIR_MESSAGES[data?.error] ?? GENERICO };
}

/** Más nuevas primero, por instante. @param {Array<{ emitida_at: string }>} items */
function masNuevasPrimero(items) {
    return [...items].sort((a, b) => Date.parse(b.emitida_at) - Date.parse(a.emitida_at));
}

/** @returns {Promise<{ ok: true, items: Array<Record<string, any>> } | { ok: false, mensaje: string }>} */
export async function cargarVigentes() {
    try {
        return { ok: true, items: masNuevasPrimero(await listarVigentes()) };
    } catch (err) {
        reportar(err, 'no se pudieron leer las vigentes');
        return { ok: false, mensaje: mensajeDeError(err, 'No se pudieron cargar las gift cards.') };
    }
}

/**
 * Filtro por "para", sin mayúsculas ni tildes (spec B2).
 * @template {{ para: string }} T
 * @param {T[]} items
 * @param {string} texto
 * @returns {T[]}
 */
export function filtrarPorPara(items, texto) {
    const q = normalizar(texto);
    if (!q) return items;
    return items.filter((g) => normalizar(g.para).includes(q));
}

/** @param {string} id @returns {Promise<{ ok: true } | { ok: false, mensaje: string }>} */
export async function canjear(id) {
    let data;
    try {
        data = await canjearGiftcard(id);
    } catch (err) {
        reportar(err, 'no se pudo canjear');
        return { ok: false, mensaje: mensajeDeError(err) };
    }
    if (data?.ok) return { ok: true };
    if (!CANJEAR_MESSAGES[data?.error]) reportar(new Error(`canjear_giftcard: respuesta inesperada ${JSON.stringify(data)}`), 'respuesta inesperada');
    return { ok: false, mensaje: CANJEAR_MESSAGES[data?.error] ?? GENERICO };
}

/**
 * Admin: las de un estado, más nuevas primero, y los totales de todos los estados.
 * @param {'vigentes'|'canjeadas'|'vencidas'|'anuladas'} estado
 * @returns {Promise<{ ok: true, items: Array<Record<string, any>>, totales: Record<string, { cantidad: number, monto: number }> } | { ok: false, mensaje: string }>}
 */
export async function cargarGiftcards(estado) {
    let data;
    try {
        data = await listarGiftcards(estado);
    } catch (err) {
        reportar(err, 'no se pudieron leer las gift cards');
        return { ok: false, mensaje: mensajeDeError(err, 'No se pudieron cargar las gift cards.') };
    }
    if (!data?.ok) {
        reportar(new Error(`listar_giftcards: respuesta inesperada ${JSON.stringify(data)}`), 'respuesta inesperada');
        return { ok: false, mensaje: 'No se pudieron cargar las gift cards.' };
    }
    return { ok: true, items: masNuevasPrimero(data.items ?? []), totales: data.totales ?? {} };
}

/** @param {{ id: string, motivo: string }} params @returns {Promise<{ ok: true } | { ok: false, mensaje: string }>} */
export async function anular({ id, motivo }) {
    const limpio = opcional(motivo);
    if (!limpio) return { ok: false, mensaje: ANULAR_MESSAGES.motivo_requerido };
    let data;
    try {
        data = await anularGiftcard({ id, motivo: limpio });
    } catch (err) {
        reportar(err, 'no se pudo anular');
        return { ok: false, mensaje: mensajeDeError(err) };
    }
    if (data?.ok) return { ok: true };
    if (!ANULAR_MESSAGES[data?.error]) reportar(new Error(`anular_giftcard: respuesta inesperada ${JSON.stringify(data)}`), 'respuesta inesperada');
    return { ok: false, mensaje: ANULAR_MESSAGES[data?.error] ?? GENERICO };
}
